import React, { useMemo, useState } from "react";
import { Dialog, IconButton, useTheme, Divider } from "@mui/material";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import { Controller, useForm } from "react-hook-form";

import SoftBox from "../SoftBox";
import SoftInput from "../SoftInput";
import SoftButton from "../SoftButton";
import SoftTypography from "../SoftTypography";
import { StyledPaper } from "../../pages/ManualEntry/manualEntryStyles";
import { CREATE_LINKS } from "../../api/links";
import { UseKpiRefresh } from "../../hooks/kpi";
import { UseCompanyId } from "../../hooks/auth";

const AddLinkModal = ({
  isOpen,
  kpiId,
  kpiLinksData,
  onModalClose,
}) => {
  const theme = useTheme();
  const companyId = UseCompanyId();

  const {
    primary: { main: primaryColor, focus: brighterPrimaryColor },
    secondary: { main: secondaryColor },
    tertiary: { main: tertiaryColor },
  } = theme.palette;

  const handleClose = () => {
    onModalClose({
      kpiLinks: null,
    });
  };

  const CustomKpiSchema = Yup.object().shape({
    text: Yup.string().required("Field is required"),
    link: Yup.string().required("Field is required"),
  });

  const validationOptions = {
    resolver: yupResolver(CustomKpiSchema),
    shouldUnregister: false,
    defaultValues: {
      text: "",
      type: "web-address",
      link: "",
    },
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm(validationOptions);

  const refreshKpis = UseKpiRefresh();

  const [currentKpiLinksData, setKpiLinksData] = useState({
    kpiId: null,
    kpiLinks: null,
  });

  const handleRegister = async (formData) => {
    if (formData.type === "e-mail") {
      formData = {
        ...formData,
        link: `mailto:${formData.link}`,
      };
    } else if (!formData.link.includes("https://")) {
      formData = {
        ...formData,
        link: `https://${formData.link}`,
      };
    }

    const payload = {};
    if (kpiLinksData && kpiLinksData?.kpiLinks?.length) {
      payload.links = [{ ...formData }, ...kpiLinksData.kpiLinks];
    } else {
      payload.links = [{ ...formData }];
    }

    try {
      await CREATE_LINKS(kpiId, companyId, formData);

      setKpiLinksData((prev) => ({ ...prev, kpiLinks: formData.links }));
      refreshKpis(payload);
    } catch (err) {
      console.log(err);
    }
    handleClose();
  };

  return (
    <Dialog
      aria-labelledby="unstyled-modal-title"
      aria-describedby="unstyled-modal-description"
      open={isOpen}
      onClose={handleClose}
      sx={{
        ".MuiPaper-root": {
          padding: { mini: "1rem", md: "1rem 1.5rem" },
          borderRadius: "8px",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
          boxShadow:
            "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
          backdropFilter: "blur(18px) saturate(160%)",
          width: "100%",
          maxWidth: "600px",
          border: "1px solid rgba(255,255,255,0.6)",
        },
      }}
    >
      <SoftBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ marginBottom: "10px" }}
      >
        <SoftTypography
          variant="h4"
          sx={{ fontSize: "20px", fontWeight: 500, color: "#0F172A" }}
        >
          Add Link
        </SoftTypography>
        <SoftButton
          variant="outlined"
          onClick={handleClose}
          sx={{
            "& svg": { fontSize: "1.25rem !important" },
            borderRadius: "50%",
            minWidth: "max-content",
            minHeight: "max-content",
            padding: "0.25rem",
            color: "#475467",
          }}
        >
          <CloseRoundedIcon />
        </SoftButton>
      </SoftBox>
      <Divider
        sx={{
          backgroundColor: "rgba(15,23,42,0.10)",
        }}
      />
      <form onSubmit={handleSubmit(handleRegister)}>
        <SoftTypography
          component="p"
          mb="5px"
          sx={{
            color: "#475467",
            fontSize: "15px",
          }}
        >
          Text to display
        </SoftTypography>
        <SoftInput
          {...register("text")}
          error={!!errors?.text}
          helperText={errors?.text?.message}
          sx={{
            backgroundColor: "#F6F7F9 !important",
            border: "1px solid #E4E7EC",
            color: "#1d2939 !important",
            "& .MuiInputBase-input::placeholder": {
              color: "#98A2B3 !important",
              opacity: "1",
            },
            "&.Mui-focused": {
              borderColor: primaryColor,
              boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
            },
          }}
        />

        <SoftBox display="flex" alignItems="center" mb="20px" mt="20px">
          <FormLabel
            sx={{
              color: "#475467",
              fontSize: "15px",
              marginRight: "20px",
              top: "0 !important",
            }}
          >
            Link to:
          </FormLabel>
          <Controller
            rules={{ required: true }}
            control={control}
            name="type"
            render={({ field }) => (
              <RadioGroup
                {...field}
                row
                onChange={(e) => setValue("type", e.target.value)}
              >
                <FormControlLabel
                  value="web-address"
                  control={
                    <Radio
                      sx={{
                        "&.Mui-checked": {
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='15px' width='15px'%3E%3Ccircle cx='50%' cy='50%' r='3' fill='%23fff' /%3E%3C/svg%3E"),linear-gradient(310deg, ${secondaryColor}, ${tertiaryColor})`,
                          borderColor: `${brighterPrimaryColor} !important`,
                        },
                      }}
                    />
                  }
                  label="Web address"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      color: "#475467 !important",
                    },
                  }}
                />
                <FormControlLabel
                  value="e-mail"
                  control={
                    <Radio
                      sx={{
                        "&.Mui-checked": {
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='15px' width='15px'%3E%3Ccircle cx='50%' cy='50%' r='3' fill='%23fff' /%3E%3C/svg%3E"),linear-gradient(310deg, ${secondaryColor}, ${tertiaryColor})`,
                          borderColor: `${brighterPrimaryColor} !important`,
                        },
                      }}
                    />
                  }
                  label="E-mail"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      color: "#475467 !important",
                    },
                  }}
                />
              </RadioGroup>
            )}
          />
        </SoftBox>

        <SoftInput
          {...register("link")}
          error={!!errors?.link}
          helperText={errors?.link?.message}
          sx={{
            backgroundColor: "#F6F7F9 !important",
            border: "1px solid #E4E7EC",
            color: "#1d2939 !important",
            "& .MuiInputBase-input::placeholder": {
              color: "#98A2B3 !important",
              opacity: "1",
            },
            "&.Mui-focused": {
              borderColor: primaryColor,
              boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
            },
          }}
        />
        <SoftTypography
          component="p"
          sx={{
            fontSize: "14px",
            color: "#475467",
            lineHeight: "17px",
            width: "530px",
            margin: "20px 0px 30px 0px",
          }}
        >
          Not sure what to put in the box? First, find the page on the web that
          you want to link to. (A search engine might be useful.) Then, copy the
          web address from the box in your browser's address bar, and paste it
          into the box above
        </SoftTypography>
        <Divider
          sx={{
            backgroundColor: "rgba(15,23,42,0.10)",
          }}
        />
        <SoftBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap="1rem"
        >
          <SoftButton
            variant="outlined"
            sx={{
              width: "100%",
              textTransform: "none",
              color: "#6C757D",
              "&:hover": {
                textDecoration: "underline",
                color: "#6C757D",
              },
            }}
            onClick={() => {
              handleClose();
            }}
          >
            Cancel
          </SoftButton>
          <SoftButton
            variant="gradient"
            sx={{ textTransform: "none", width: "100%" }}
            color="primary"
            type="submit"
          >
            Add
          </SoftButton>
        </SoftBox>
      </form>
    </Dialog>
  );
};

export default AddLinkModal;
